/**
 * PhotoMetadata Component
 *
 * Displays photo EXIF metadata in a configurable 2-column grid layout.
 * Supports two variants: full (6 fields) and slim (4 fields).
 *
 * @module components/PhotoMetadata
 */

import type { PhotoMetadataProps } from './PhotoMetadata.types';
import {
  formatCamera,
  formatLens,
  formatFocalLength,
  formatAperture,
  formatShutterSpeed,
  formatISO,
} from '@/utils/exif';
import logger from '@/utils/logger';

/**
 * PhotoMetadata Component
 *
 * Displays photo EXIF metadata with two display variants:
 * - full: Camera, Lens, FL, Aperture, Shutter, ISO (6 fields)
 * - slim: FL, Aperture, Shutter, ISO (4 fields)
 *
 * Features:
 * - Handles null/missing metadata gracefully
 * - Displays "n/a" for missing individual fields
 * - Logs warnings for malformed data
 * - Responsive layout (2-column → 1-column for narrow containers)
 */
export function PhotoMetadata({
  photoData,
  variant = 'full',
  className = '',
}: PhotoMetadataProps): JSX.Element | null {
  // Return null if photoData is missing or metadata is missing (FR-005a)
  if (!photoData || !photoData.metadata) {
    return null;
  }

  const { metadata } = photoData;

  /**
   * Format a metadata field with error handling
   * Returns formatted value or "n/a" for missing/malformed data
   */
  const formatField = (
    formatter: (metadata: any) => string | null,
    fieldName: string
  ): string => {
    try {
      const value = formatter(metadata);
      if (value === null || value === undefined) {
        return 'n/a';
      }
      return value;
    } catch (error) {
      // Log warning for malformed data (FR-014a)
      logger.warn('PhotoMetadata', `Malformed EXIF field: ${fieldName}`, {
        photoId: photoData.globalId,
        fieldName,
        error: error instanceof Error ? error.message : String(error),
      });
      return 'n/a';
    }
  };

  // Format all fields
  const camera = formatField(formatCamera, 'camera');
  const lens = formatField((m) => formatLens(m, true), 'lens');
  const focalLength = formatField(formatFocalLength, 'focalLength');
  const aperture = formatField(formatAperture, 'aperture');
  const shutterSpeed = formatField(formatShutterSpeed, 'shutterSpeed');
  const iso = formatField(formatISO, 'iso');

  // Determine which fields to display based on variant
  const showFullVariant = variant === 'full';

  // TODO, nicer rendering for camera
  // TODO, dropping out components for when component is skinny
  // TODO, pretty layouts

  return (
    <div
      className={`metadata-grid grid gap-x-2 gap-y-0.5 text-sm text-muted leading-tight ${className}`}
      style={{
        // Use container queries for responsive layout (FR-012a)
        // Falls back to 2-column grid, switches to 1-column below 200px
        containerType: 'inline-size',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      }}
    >
      <style>{`
        @container (max-width: 200px) {
          .metadata-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      {/* Full variant: Camera and Lens (FR-001, FR-002) */}
      {showFullVariant && (
        <>
          <div className="truncate col-span-2">
            <span className="font-medium">Camera:</span> {camera}
          </div>
          <div className="truncate col-span-2">
            <span className="font-medium">Lens:</span> {lens}
          </div>
        </>
      )}

      {/* Both variants: FL, Aperture, Shutter, ISO (FR-003, FR-004, FR-005, FR-006) */}
      <div className="col-span-2 flex justify-between text-sm text-muted">
        <span><span className="font-medium"></span> {iso}</span>
        <span><span className="font-medium"></span> {focalLength}</span>
        <span><span className="font-medium"></span> {aperture}</span>
        <span><span className="font-medium"></span> {shutterSpeed}</span>
      </div>
    </div>
  );
}
